// frontend/src/components/AnalyticsDashboard.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Box,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Fade,
  Slide,
  alpha,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Stack
} from '@mui/material';
import {
  Assessment,
  Assignment,
  Schedule,
  CheckCircle,
  Refresh,
  CalendarToday,
  FilterList,
  Download,
  Share
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  LineChart,
  Line
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import DashboardCard from '../DashboardCard';
import ChartContainer from '../ChartContainer';
import api from '../../services/api';
import '../../styles/charts.css';

// Helper function to check if token is valid
const isTokenValid = () => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  
  try {
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) return false;
    
    const payload = JSON.parse(atob(tokenParts[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Check if token is expired
    if (payload.exp && payload.exp < currentTime) {
      // Token expired, remove it
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('username');
      return false;
    }
    
    return true;
  } catch (error) {
    // Invalid token, remove it
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('username');
    return false;
  }
};

const UserDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [cardLoading, setCardLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [users, setUsers] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [selectedChartItem, setSelectedChartItem] = useState(null);
  const theme = useTheme();
  const navigate = useNavigate();

  // Initialize user role and fetch users list (for admin filter)
  useEffect(() => {
    // Check token validity first
    if (!isTokenValid()) {
      console.warn('Token expired or invalid - redirecting to login');
      navigate('/login');
      return;
    }
    
    const role = localStorage.getItem('role');
    setUserRole(role);
    
    if (role === 'admin') {
      // Retry function for network errors (handles 525, CORS issues on first request)
      const fetchUsersWithRetry = async (retryCount = 0, maxRetries = 2) => {
        try {
          const res = await api.get('/users');
          setUsers(res.data);
        } catch (err) {
          console.error('Error fetching users:', err);
          if (err.response?.status === 401) {
            navigate('/login');
          } else if ((err.code === 'ERR_NETWORK' || !err.response) && retryCount < maxRetries) {
            // Network error (CORS, 525, etc.) - retry after a short delay
            console.warn(`Network error fetching users (attempt ${retryCount + 1}/${maxRetries + 1}) - retrying...`);
            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
            return fetchUsersWithRetry(retryCount + 1, maxRetries);
          } else if (err.code === 'ERR_NETWORK' || !err.response) {
            // Final attempt failed - log but don't break the UI
            console.warn('Network error fetching users after retries - this may be a temporary issue');
            // Users list is optional for dashboard, so we can continue without it
          }
        }
      };
      
      fetchUsersWithRetry();
    }
  }, [navigate]);

  const fetchMetrics = useCallback(async () => {
    // Check token validity before making request
    if (!isTokenValid()) {
      console.warn('Token expired - redirecting to login');
      navigate('/login');
      return;
    }
    
    // Don't fetch if userRole is not yet set
    const currentRole = userRole || localStorage.getItem('role');
    if (!currentRole) {
      console.log('Waiting for user role to be set...');
      return;
    }

    try {
      setCardLoading(true);
      setChartLoading(true);
      let url = '/complaints/metrics';
      const params = new URLSearchParams();
      
      if (dateRange && dateRange !== 'all') {
        params.append('dateRange', dateRange);
        if (dateRange === 'custom' && customStartDate && customEndDate) {
          params.append('start', customStartDate);
          params.append('end', customEndDate);
        }
      }
      
      // Add user filter for admin
      if (currentRole === 'admin' && selectedUserId && selectedUserId !== 'all') {
        params.append('userId', selectedUserId);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      console.log('Fetching metrics from:', url);
      const response = await api.get(url);
      console.log('Metrics response:', response.data);
      setMetrics(response.data);
      setError(null);
      
      // Simulate card loading animation
      setTimeout(() => setCardLoading(false), 800);
      // Chart loading slightly longer for smooth transition
      setTimeout(() => setChartLoading(false), 1000);
    } catch (err) {
      if (err.response?.status === 401) {
        // Token expired during request, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        localStorage.removeItem('username');
        navigate('/login');
        setError('Session expired. Please log in again.');
      } else {
        setError('Failed to load dashboard data');
      }
      console.error('Error fetching metrics:', err);
      console.error('Error details:', err.response?.data || err.message);
      setCardLoading(false);
      setChartLoading(false);
    }
  }, [dateRange, customStartDate, customEndDate, selectedUserId, userRole, navigate]);

  const handleRefresh = () => {
    fetchMetrics();
  };

  useEffect(() => {
    // Only fetch metrics after userRole is set
    const currentRole = userRole || localStorage.getItem('role');
    if (currentRole) {
      fetchMetrics();
    }
  }, [dateRange, customStartDate, customEndDate, selectedUserId, userRole, fetchMetrics]);

  // Note: Error handling UI is rendered below after memoized datasets

  // Build chart datasets from metrics (memoized)
  const statusCounts = metrics?.statusCounts || [];
  const categoryStats = metrics?.categoryStats || [];
  const assemblyStats = metrics?.assemblyStats || [];
  const monthlyStats = metrics?.monthlyStats || [];
  const priorityStats = metrics?.priorityStats || [];

  // Debug logging
  useEffect(() => {
    if (metrics) {
      console.log('📊 Metrics data received:', {
        statusCounts,
        categoryStats,
        assemblyStats,
        monthlyStats,
        totalComplaints: metrics.totalComplaints,
        userRole,
        selectedUserId
      });
    }
  }, [metrics, statusCounts, categoryStats, assemblyStats, monthlyStats, userRole, selectedUserId]);

  const statusColorMap = {
    New: '#42a5f5',
    Open: '#7e57c2',
    Pending: '#ff9800',
    Solved: '#26a69a',
    Resolved: '#4caf50'
  };

  const fallbackPalette = ['#42a5f5', '#7e57c2', '#66bb6a', '#ff7043', '#26c6da', '#ab47bc', '#ffa726', '#8d6e63'];

  const statusData = useMemo(() => {
    const data = Array.isArray(statusCounts)
      ? statusCounts
          .filter(s => s?._id !== 'Processing') // Filter out Processing status
          .map((s, idx) => ({
            name: s?._id || 'Unknown',
            value: s?.count || 0,
            color: statusColorMap[s?._id] || fallbackPalette[idx % fallbackPalette.length]
          }))
      : [];
    console.log('📈 Status data processed:', data);
    return data;
  }, [statusCounts]);

  const priorityColorMap = {
    Low: '#9e9e9e',
    Medium: '#29b6f6',
    High: '#ffa726',
    Urgent: '#ef5350'
  };

  const priorityData = useMemo(() => (
    Array.isArray(priorityStats)
      ? priorityStats.map((p, idx) => ({
          name: p?._id || 'Medium',
          value: p?.count || 0,
          color: priorityColorMap[p?._id] || fallbackPalette[idx % fallbackPalette.length]
        }))
      : []
  ), [priorityStats]);

  const categoryData = useMemo(() => (
    Array.isArray(categoryStats)
      ? categoryStats.map((c, idx) => ({
          name: c?._id || 'Unknown',
          value: c?.count || 0,
          color: fallbackPalette[idx % fallbackPalette.length]
        }))
      : []
  ), [categoryStats]);

  const assemblyData = useMemo(() => {
    const data = Array.isArray(assemblyStats)
      ? assemblyStats.map((a, idx) => ({
          name: a?._id || 'Unknown',
          value: a?.count || 0,
          color: fallbackPalette[idx % fallbackPalette.length]
        }))
      : [];
    console.log('🏛️ Assembly data processed:', data);
    return data;
  }, [assemblyStats]);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const trendData = useMemo(() => (
    Array.isArray(monthlyStats)
      ? monthlyStats.map((m) => ({
          month: m?._id ? `${monthNames[(m._id.month || 1) - 1]} '${String(m._id.year || new Date().getFullYear()).toString().slice(-2)}` : 'N/A',
          complaints: m?.count || 0
        }))
      : []
  ), [monthlyStats]);

  const handleExport = (key) => {
    // Enhanced export functionality with CSV support
    try {
      let dataToExport = [];
      let filename = '';
      
      switch(key) {
        case 'status':
          dataToExport = statusData;
          filename = 'complaints_by_status';
          break;
        case 'category':
          dataToExport = categoryData;
          filename = 'complaints_by_category';
          break;
        case 'trend':
          dataToExport = trendData;
          filename = 'complaint_trends';
          break;
        case 'assembly':
          dataToExport = assemblyData;
          filename = 'complaints_by_assembly';
          break;
        default:
          dataToExport = [];
      }
      
      if (dataToExport.length === 0) {
        alert('No data to export');
        return;
      }
      
      // Convert to CSV format
      const headers = Object.keys(dataToExport[0]).join(',');
      const rows = dataToExport.map(item => 
        Object.values(item).map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      const csv = `${headers}\n${rows}`;
      
      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('Exported:', filename);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export data. Please try again.');
    }
  };

  const handleFullscreen = (containerId) => {
    const el = document.getElementById(containerId);
    if (el && el.requestFullscreen) {
      el.requestFullscreen().catch(err => {
        console.error('Fullscreen error:', err);
      });
    }
  };

  // Drill-down handler for chart items
  const handleChartItemClick = (data, chartType) => {
    setSelectedChartItem({ data, chartType });
    // Navigate to filtered complaints list
    const filterParams = new URLSearchParams();
    
    if (chartType === 'status' && data.name) {
      filterParams.append('status', data.name);
    } else if (chartType === 'category' && data.name) {
      filterParams.append('category', data.name);
    } else if (chartType === 'assembly' && data.name) {
      filterParams.append('assembly', data.name);
    }
    
    navigate(`/complaints?${filterParams.toString()}`);
  };

  if (error) {
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography color="error">{error}</Typography>
        <IconButton onClick={handleRefresh} color="primary" size="large">
          <Refresh />
        </IconButton>
      </Box>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box sx={{ mt: { xs: '57px', sm: '57px', md: 0 } }}>
        {/* Header Section */}
        <Slide direction="down" in timeout={600}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              mb: 2
            }}>
              <Box>
                <Typography 
                  variant="h4" 
                  component="h1"
                  sx={{ 
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.5,
                    lineHeight: 1.2,
                    letterSpacing: 0.2
                  }}
                >
                  Analytics Dashboard
                </Typography>
                <Typography 
                  variant="subtitle1" 
                  color="text.secondary"
                  sx={{ 
                    fontWeight: 500,
                    opacity: 0.85,
                    lineHeight: 1.4
                  }}
                >
                  Monitor complaint statistics and track system performance in real-time
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={2} alignItems="center">
                {/* User Filter (Admin Only) */}
                {userRole === 'admin' && (
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="user-filter-label">Filter by User</InputLabel>
                    <Select
                      labelId="user-filter-label"
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      label="Filter by User"
                      sx={{ 
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.2)
                        }
                      }}
                    >
                      <MenuItem value="all">All Users</MenuItem>
                      {users.map((user) => (
                        <MenuItem key={user._id} value={user._id}>
                          {user.username}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                
                {/* Date Range Selector */}
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    displayEmpty
                    sx={{ 
                      backgroundColor: 'white',
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.2)
                      }
                    }}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="7days">Last 7 Days</MenuItem>
                    <MenuItem value="weekly">Last Week</MenuItem>
                    <MenuItem value="monthly">Last Month</MenuItem>
                    <MenuItem value="yearly">Last Year</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>
                </FormControl>

                {/* Custom Date Range Inputs */}
                {dateRange === 'custom' && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      type="date"
                      size="small"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      sx={{ 
                        backgroundColor: 'white',
                        borderRadius: 2
                      }}
                    />
                    <TextField
                      type="date"
                      size="small"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      sx={{ 
                        backgroundColor: 'white',
                        borderRadius: 2
                      }}
                    />
                  </Box>
                )}

                <Stack direction="row" spacing={1}>
                  <Tooltip title="Export Data">
                    <IconButton onClick={() => {}} sx={{ backgroundColor: 'white' }}>
                      <Download />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share Dashboard">
                    <IconButton onClick={() => {}} sx={{ backgroundColor: 'white' }}>
                      <Share />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Refresh Data">
                    <IconButton onClick={handleRefresh} sx={{ backgroundColor: 'white' }}>
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Slide>

        {/* Summary Stats */}
        <Slide direction="up" in timeout={700}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexWrap: 'wrap',
            mt: 3,
            mb: 2
          }}>
            {userRole === 'admin' && selectedUserId !== 'all' && (
              <Chip 
                icon={<FilterList />} 
                label={`User: ${users.find(u => u._id === selectedUserId)?.username || 'Unknown'}`}
                variant="outlined"
                color="secondary"
                sx={{ mb: 1 }}
                onDelete={() => setSelectedUserId('all')}
              />
            )}
            <Chip 
              icon={<CalendarToday />} 
              label={`Date Range: ${dateRange === 'all' ? 'All Time' : dateRange}`}
              variant="outlined"
              color="primary"
              sx={{ mb: 1 }}
            />
            <Chip 
              icon={<Assessment />} 
              label={`Total Metrics: ${Object.keys(metrics || {}).length}`}
              variant="outlined"
              color="info"
              sx={{ mb: 1 }}
            />
            {metrics?.lastUpdated && (
              <Chip 
                label={`Last Updated: ${new Date(metrics.lastUpdated).toLocaleString()}`}
                variant="outlined"
                color="default"
                sx={{ mb: 1 }}
              />
            )}
          </Box>
        </Slide>

        {/* Metrics Cards */}
        <Slide direction="up" in timeout={700}>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <DashboardCard
                title="Total Complaints"
                value={metrics?.totalComplaints || 0}
                icon={<Assessment />}
                color="primary"
                trend={12}
                subtitle="All time complaints"
                loading={cardLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DashboardCard
                title="New Complaints"
                value={metrics?.newComplaints || 0}
                icon={<Assignment />}
                color="info"
                trend={8}
                subtitle="This month"
                loading={cardLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DashboardCard
                title="Pending Complaints"
                value={metrics?.pendingComplaints || 0}
                icon={<Schedule />}
                color="warning"
                trend={-5}
                subtitle="Awaiting resolution"
                loading={cardLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DashboardCard
                title="Resolved Complaints"
                value={metrics?.resolvedComplaints || 0}
                icon={<CheckCircle />}
                color="success"
                trend={15}
                subtitle="Successfully closed"
                loading={cardLoading}
              />
            </Grid>
          </Grid>
        </Slide>

        {/* Charts Section */}
        <Slide direction="up" in timeout={800}>
          <Grid container spacing={3}>
            {/* Status Distribution Chart */}
            <Grid item xs={12} lg={6}>
              <ChartContainer
                title="Complaints by Status"
                subtitle="Distribution of complaints across different statuses"
                height={400}
                loading={chartLoading}
                onRefresh={handleRefresh}
                onExport={() => handleExport('status')}
                onFullscreen={() => handleFullscreen('status-chart')}
              >
                <div id="status-chart" style={{ width: '100%', height: '400px', margin: 0, padding: 0, overflow: 'hidden' }}>
                  {statusData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={statusData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          innerRadius={40}
                          fill="#8884d8"
                          dataKey="value"
                          animationBegin={0}
                          animationDuration={800}
                          animationEasing="ease-out"
                          isAnimationActive={true}
                          onClick={(data) => handleChartItemClick(data, 'status')}
                          aria-label="Complaints by Status pie chart"
                        >
                          {statusData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.color}
                              style={{
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer'
                              }}
                            />
                          ))}
                        </Pie>
                        <RechartsTooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              const total = statusData.reduce((sum, d) => sum + d.value, 0);
                              const percentage = ((data.value / total) * 100).toFixed(1);
                              return (
                                <Box
                                  sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                    border: '1px solid rgba(0, 0, 0, 0.1)',
                                    borderRadius: 2,
                                    padding: 2,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                    {data.name}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: data.color, fontWeight: 500, mb: 0.5 }}>
                                    Count: {data.value}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {percentage}% of total
                                  </Typography>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: 20 }}
                          iconType="circle"
                          formatter={(value, entry) => (
                            <span style={{ color: entry.color, fontWeight: 500 }}>{value}</span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                      <Typography variant="body2" color="text.secondary">
                        No data available for the selected time range
                      </Typography>
                    </Box>
                  )}
                </div>
              </ChartContainer>
            </Grid>

            {/* Priority Distribution Chart */}
            <Grid item xs={12} lg={6}>
              <ChartContainer
                title="Complaints by Priority"
                subtitle="Distribution of complaints across priority levels"
                height={400}
                loading={chartLoading}
                onRefresh={handleRefresh}
              >
                <div id="priority-chart" style={{ width: '100%', height: '400px', margin: 0, padding: 0, overflow: 'hidden' }}>
                  {priorityData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={priorityData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          innerRadius={40}
                          dataKey="value"
                          isAnimationActive={true}
                          aria-label="Complaints by Priority pie chart"
                        >
                          {priorityData.map((entry, index) => (
                            <Cell key={`priority-cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend
                          wrapperStyle={{ paddingTop: 20 }}
                          iconType="circle"
                          formatter={(value, entry) => (
                            <span style={{ color: entry.color, fontWeight: 500 }}>{value}</span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                      <Typography variant="body2" color="text.secondary">
                        No data available for the selected time range
                      </Typography>
                    </Box>
                  )}
                </div>
              </ChartContainer>
            </Grid>

            {/* Category Distribution Chart */}
            <Grid item xs={12} lg={6}>
              <ChartContainer
                title="Complaints by Category"
                subtitle="Breakdown of complaints by category type"
                height={400}
                loading={chartLoading}
                onRefresh={handleRefresh}
                onExport={() => handleExport('category')}
                onFullscreen={() => handleFullscreen('category-chart')}
              >
                <div id="category-chart" style={{ width: '100%', height: '400px', margin: 0, padding: 0, overflow: 'hidden' }}>
                  {categoryData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={categoryData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 60,
                        }}
                      >
                        <defs>
                          {categoryData.map((entry, index) => (
                            <linearGradient key={`gradient-${index}`} id={`category-gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={entry.color} stopOpacity={1}/>
                              <stop offset="100%" stopColor={entry.color} stopOpacity={0.6}/>
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid 
                          strokeDasharray="3 3" 
                          stroke="#e0e0e0"
                          vertical={false}
                        />
                        <XAxis 
                          dataKey="name" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ fontSize: 12 }}
                          aria-label="Category names"
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          allowDecimals={false}
                          aria-label="Number of complaints"
                        />
                        <RechartsTooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <Box
                                  sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                    border: '1px solid rgba(0, 0, 0, 0.1)',
                                    borderRadius: 2,
                                    padding: 1.5,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {data.name}
                                  </Typography>
                                  <Typography variant="body1" sx={{ color: data.color, fontWeight: 600 }}>
                                    {data.value} complaints
                                  </Typography>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: 10 }}
                          iconType="rect"
                        />
                        <Bar 
                          dataKey="value" 
                          radius={[8, 8, 0, 0]}
                          animationDuration={800}
                          animationEasing="ease-out"
                          onClick={(data) => handleChartItemClick(data, 'category')}
                        >
                          {categoryData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={`url(#category-gradient-${index})`}
                              style={{
                                cursor: 'pointer',
                                transition: 'opacity 0.3s ease'
                              }}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                      <Typography variant="body2" color="text.secondary">
                        No data available for the selected time range
                      </Typography>
                    </Box>
                  )}
                </div>
              </ChartContainer>
            </Grid>

            {/* Trend Analysis Chart */}
            <Grid item xs={12} lg={6}>
              <ChartContainer
                title="Complaint Trends"
                subtitle="Monthly complaint trends over the last 6 months"
                height={400}
                loading={chartLoading}
                onRefresh={handleRefresh}
                onExport={() => handleExport('trend')}
                onFullscreen={() => handleFullscreen('trend-chart')}
              >
                <div id="trend-chart" style={{ width: '100%', height: '400px', margin: 0, padding: 0, overflow: 'hidden' }}>
                  {trendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart
                        data={trendData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <defs>
                          <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid 
                          strokeDasharray="3 3" 
                          stroke="#e0e0e0"
                          vertical={false}
                        />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          allowDecimals={false}
                        />
                        <RechartsTooltip
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              const currentIndex = trendData.findIndex(d => d.month === label);
                              const prevValue = currentIndex > 0 ? trendData[currentIndex - 1]?.complaints : null;
                              const trend = prevValue !== null ? (data.complaints > prevValue ? '↑' : data.complaints < prevValue ? '↓' : '→') : '';
                              const trendColor = prevValue !== null 
                                ? (data.complaints > prevValue ? '#4caf50' : data.complaints < prevValue ? '#f44336' : '#ff9800')
                                : 'inherit';
                              const diff = prevValue !== null ? Math.abs(data.complaints - prevValue) : 0;
                              
                              return (
                                <Box
                                  sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                    border: '1px solid rgba(0, 0, 0, 0.1)',
                                    borderRadius: 2,
                                    padding: 1.5,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {label}
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {data.complaints} complaints
                                  </Typography>
                                  {prevValue !== null && diff > 0 && (
                                    <Typography variant="caption" sx={{ color: trendColor, fontWeight: 500 }}>
                                      {trend} {diff} from previous month
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="complaints"
                          stroke="#8884d8"
                          strokeWidth={3}
                          dot={{ fill: '#8884d8', strokeWidth: 2, r: 5 }}
                          activeDot={{ r: 8, stroke: '#8884d8', strokeWidth: 2 }}
                          animationDuration={800}
                          animationEasing="ease-out"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                      <Typography variant="body2" color="text.secondary">
                        No data available for the selected time range
                      </Typography>
                    </Box>
                  )}
                </div>
              </ChartContainer>
            </Grid>

            {/* Assembly Distribution Chart */}
            <Grid item xs={12} lg={6}>
              <ChartContainer
                title="Complaints by Assembly"
                subtitle="Distribution of complaints across different assemblies"
                height={400}
                loading={chartLoading}
                onRefresh={handleRefresh}
                onExport={() => handleExport('assembly')}
                onFullscreen={() => handleFullscreen('assembly-chart')}
              >
                <div id="assembly-chart" style={{ width: '100%', height: '400px', margin: 0, padding: 0, overflow: 'hidden' }}>
                  {assemblyData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={assemblyData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 60,
                        }}
                      >
                        <defs>
                          {assemblyData.map((entry, index) => (
                            <linearGradient key={`gradient-${index}`} id={`assembly-gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={entry.color} stopOpacity={1}/>
                              <stop offset="100%" stopColor={entry.color} stopOpacity={0.6}/>
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid 
                          strokeDasharray="3 3" 
                          stroke="#e0e0e0"
                          vertical={false}
                        />
                        <XAxis 
                          dataKey="name" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ fontSize: 12 }}
                          aria-label="Category names"
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          allowDecimals={false}
                          aria-label="Number of complaints"
                        />
                        <RechartsTooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <Box
                                  sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                    border: '1px solid rgba(0, 0, 0, 0.1)',
                                    borderRadius: 2,
                                    padding: 1.5,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {data.name}
                                  </Typography>
                                  <Typography variant="body1" sx={{ color: data.color, fontWeight: 600 }}>
                                    {data.value} complaints
                                  </Typography>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: 10 }}
                          iconType="rect"
                        />
                        <Bar 
                          dataKey="value" 
                          radius={[8, 8, 0, 0]}
                          animationDuration={800}
                          animationEasing="ease-out"
                          onClick={(data) => handleChartItemClick(data, 'assembly')}
                        >
                          {assemblyData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={`url(#assembly-gradient-${index})`}
                              style={{
                                cursor: 'pointer',
                                transition: 'opacity 0.3s ease'
                              }}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                      <Typography variant="body2" color="text.secondary">
                        No data available for the selected time range
                      </Typography>
                    </Box>
                  )}
                </div>
              </ChartContainer>
            </Grid>
          </Grid>
        </Slide>

        {/* Quick Actions */}
        <Slide direction="up" in timeout={900}>
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  onClick={() => navigate('/complaint_new')}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    color: 'white',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 20px 40px rgba(25, 118, 210, 0.3)'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Assignment sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                      New Complaint
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Create a new complaint entry
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  onClick={() => navigate('/complaints')}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #0288d1 0%, #03a9f4 100%)',
                    color: 'white',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 20px 40px rgba(2, 136, 209, 0.3)'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Assessment sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                      View All
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Browse all complaints
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  onClick={() => navigate('/pending-complaints')}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
                    color: 'white',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 20px 40px rgba(245, 124, 0, 0.3)'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Schedule sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                      Pending
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Review pending complaints
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card 
                  onClick={() => navigate('/resolved-complaints')}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                    color: 'white',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 20px 40px rgba(56, 142, 60, 0.3)'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <CheckCircle sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                      Resolved
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      View resolved complaints
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Slide>
      </Box>
    </Fade>
  );
};

export default UserDashboard;
